export type ActionKey = 'forward' | 'back' | 'left' | 'right' | 'run' | 'jump' | 'interact' | 'photo';

const DEFAULT_BINDINGS: Record<ActionKey, number[]> = {
  forward: ['W'.charCodeAt(0)],
  back: ['S'.charCodeAt(0)],
  left: ['A'.charCodeAt(0)],
  right: ['D'.charCodeAt(0)],
  run: [16],
  jump: [32],
  interact: [],
  photo: ['P'.charCodeAt(0)],
};

export interface PointerLockState {
  locked: boolean;
  deltaX: number;
  deltaY: number;
}

export class InputManager {
  private canvas: HTMLCanvasElement;
  private bindings: Record<ActionKey, number[]> = { ...DEFAULT_BINDINGS };
  private held = new Set<number>();
  private pressedThisFrame = new Set<number>();
  private releasedThisFrame = new Set<number>();

  private mouseDeltaX = 0;
  private mouseDeltaY = 0;
  private interactQueued = false;
  private interactHeld = false;
  private interactReleased = false;
  private pointerLocked = false;
  private suspended = false;

  /** Matikan input gameplay (dipakai saat chat terbuka). */
  setSuspended(suspended: boolean): void {
    this.suspended = suspended;
  }

  private onKeyDown = (e: KeyboardEvent) => {
    if (!this.held.has(e.keyCode)) {
      this.pressedThisFrame.add(e.keyCode);
    }
    this.held.add(e.keyCode);
    if (e.keyCode === 32 || e.keyCode === 16) e.preventDefault();
  };

  private onKeyUp = (e: KeyboardEvent) => {
    this.held.delete(e.keyCode);
    this.releasedThisFrame.add(e.keyCode);
  };

  private onMouseMove = (e: MouseEvent) => {
    if (!this.pointerLocked) return;
    this.mouseDeltaX += e.movementX;
    this.mouseDeltaY += e.movementY;
  };

  private onMouseDown = (e: MouseEvent) => {
    if (!this.pointerLocked) return;
    if (e.button === 0) {
      this.interactQueued = true;
      this.interactHeld = true;
    }
  };

  private onMouseUp = (e: MouseEvent) => {
    if (e.button === 0) {
      this.interactHeld = false;
      this.interactReleased = true;
    }
  };

  private onPointerLockChange = () => {
    this.pointerLocked = document.pointerLockElement === this.canvas;
    if (!this.pointerLocked) {
      this.held.clear();
      this.mouseDeltaX = 0;
      this.mouseDeltaY = 0;
      this.interactQueued = false;
      this.interactHeld = false;
      this.interactReleased = false;
    }
  };

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mousedown', this.onMouseDown);
    document.addEventListener('mouseup', this.onMouseUp);
    document.addEventListener('pointerlockchange', this.onPointerLockChange);
  }

  static isPointerLockSupported(): boolean {
    return 'pointerLockElement' in document && typeof document.body?.requestPointerLock === 'function';
  }

  requestPointerLock(): void {
    if (!InputManager.isPointerLockSupported()) return;
    void this.canvas.requestPointerLock();
  }

  exitPointerLock(): void {
    if (document.pointerLockElement === this.canvas) {
      document.exitPointerLock();
    }
  }

  get isPointerLocked(): boolean {
    return this.pointerLocked;
  }

  isDown(action: ActionKey): boolean {
    if (this.suspended) return false;
    return this.bindings[action].some((code) => this.held.has(code));
  }

  wasPressed(action: ActionKey): boolean {
    if (this.suspended) return false;
    return this.bindings[action].some((code) => this.pressedThisFrame.has(code));
  }

  consumeInteract(): boolean {
    const q = this.interactQueued;
    this.interactQueued = false;
    if (this.suspended) return false;
    return q;
  }

  isInteractHeld(): boolean {
    return this.interactHeld;
  }

  consumeInteractRelease(): boolean {
    const r = this.interactReleased;
    this.interactReleased = false;
    if (this.suspended) return false;
    return r;
  }

  consumeMouseDelta(): { x: number; y: number } {
    if (this.suspended) {
      this.mouseDeltaX = 0;
      this.mouseDeltaY = 0;
      return { x: 0, y: 0 };
    }
    const d = { x: this.mouseDeltaX, y: this.mouseDeltaY };
    this.mouseDeltaX = 0;
    this.mouseDeltaY = 0;
    return d;
  }

  rebind(action: ActionKey, keyCodes: number[]): void {
    this.bindings[action] = [...keyCodes];
  }

  endFrame(): void {
    this.pressedThisFrame.clear();
    this.releasedThisFrame.clear();
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mousedown', this.onMouseDown);
    document.removeEventListener('mouseup', this.onMouseUp);
    document.removeEventListener('pointerlockchange', this.onPointerLockChange);
  }
}